import { css } from 'aphrodite';
import { IAIPromptTemplate, getAIPromptText, isAccountScopedPrompt } from '../../../../models/AIPromptTemplate';
import { Button } from '@HoudiniUI/Button';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
	DropdownSeparator,
} from '@HoudiniUI/Dropdown';
import { ExpandableText } from '@HoudiniUI/ExpandableText';
import { styleSheet } from './styles';

export interface ISavedPromptActions {
	onDelete: (prompt: IAIPromptTemplate) => void;
	onRename: (prompt: IAIPromptTemplate) => void;
	onToggleShared: (prompt: IAIPromptTemplate) => void;
}

interface IProps {
	prompt: IAIPromptTemplate;
	onUse: (prompt: IAIPromptTemplate) => void;
	/** The signed-in user's id: an account's prompts belong to different people, and only the creator edits one. */
	currentUserId?: string;
	/** Present for a saved prompt, absent for a curated sample. */
	savedActions?: ISavedPromptActions;
}

/**
 * Who a saved prompt reaches. The creator's name is not on the template payload (only their id), so a teammate's
 * shared prompt reads as shared rather than naming who shared it.
 */
const ownerLine = (prompt: IAIPromptTemplate, currentUserId?: string) => {
	const isMine = !!currentUserId && prompt.creator?.id === currentUserId;
	if (!isAccountScopedPrompt(prompt)) {
		return 'Only you';
	}
	return isMine ? 'You shared this with the team' : 'Shared with the team';
};

export const PromptCard: React.FC<IProps> = ({ prompt, onUse, currentUserId, savedActions }) => {
	const isMine = !!currentUserId && prompt.creator?.id === currentUserId;
	const shared = isAccountScopedPrompt(prompt);
	return (
		<div className={css(styleSheet.promptCard)}>
			<div className={css(styleSheet.promptCardHeader)}>
				<h3 className={css(styleSheet.promptCardName)}>{prompt.name}</h3>
				{savedActions && isMine ? (
					<Dropdown>
						<DropdownEllipsisTrigger aria-label={`Actions for ${prompt.name}`} />
						<DropdownContent align='end'>
							<DropdownItem onClick={() => savedActions.onRename(prompt)}>Rename</DropdownItem>
							<DropdownItem onClick={() => savedActions.onToggleShared(prompt)}>
								{shared ? 'Make private' : 'Share with the team'}
							</DropdownItem>
							<DropdownSeparator />
							<DropdownItem onClick={() => savedActions.onDelete(prompt)}>Delete</DropdownItem>
						</DropdownContent>
					</Dropdown>
				) : null}
			</div>
			{savedActions ? (
				<p className={css(styleSheet.promptCardMeta)}>{ownerLine(prompt, currentUserId)}</p>
			) : prompt.summary ? (
				<p className={css(styleSheet.promptCardSummary)}>{prompt.summary}</p>
			) : null}
			<ExpandableText text={getAIPromptText(prompt)} maxLines={2} />
			<div className={css(styleSheet.promptCardActions)}>
				<Button aria-label={`Use prompt ${prompt.name}`} onClick={() => onUse(prompt)} size='sm' variant='secondary'>
					Use prompt
				</Button>
			</div>
		</div>
	);
};
